import { useEffect, useState } from "react";
import { Plus, Trash2, Edit3, Loader2, Phone, Mail, X, Save } from "lucide-react";
import { toast } from "sonner";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const emptyNew = { name: "", email: "", phone: "", password: "" };

export default function Employees() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyNew);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/employees");
      setList(data);
    } catch (err) {
      toast.error("Failed to load employees");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password required");
    if (form.password.length < 6) return toast.error("Password must be ≥ 6 chars");
    setCreating(true);
    try {
      await api.post("/admin/employees", form);
      toast.success("Pilot account created");
      setForm(emptyNew);
      setOpenNew(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create");
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this employee? Their open jobs will become unassigned.")) return;
    try {
      await api.delete(`/admin/employees/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleDisabled = async (emp) => {
    try {
      await api.patch(`/admin/employees/${emp.id}`, { disabled: !emp.disabled });
      load();
    } catch { toast.error("Update failed"); }
  };

  const saveEdit = async () => {
    const payload = { name: editing.name, phone: editing.phone };
    if (editing.newPassword) payload.password = editing.newPassword;
    try {
      await api.patch(`/admin/employees/${editing.id}`, payload);
      toast.success("Employee updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div data-testid="employees-page" className="min-h-screen bg-muted/30">
      <AdminNav />
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-primary font-bold">Employees</h1>
            <p className="text-muted-foreground mt-1">Manage your pilot accounts. Pilots can log in and see jobs assigned to them.</p>
          </div>
          <Button onClick={() => setOpenNew(true)} data-testid="add-employee-btn" className="rounded-full bg-primary hover:bg-secondary text-primary-foreground">
            <Plus className="h-4 w-4 mr-1.5" /> Add pilot
          </Button>
        </div>

        <div className="bg-card border border-border rounded-3xl shadow-card overflow-hidden">
          {loading ? (
            <div className="py-20 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : list.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">No employees yet. Add your first pilot.</div>
          ) : (
            <div className="divide-y divide-border">
              {list.map((e, i) => (
                <div key={e.id} data-testid={`employee-row-${i}`} className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="font-display text-lg font-bold text-primary">
                      {e.name}
                      {e.disabled && <span className="ml-2 text-xs text-destructive font-semibold">DISABLED</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {e.email}</span>
                      {e.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {e.phone}</span>}
                      <span>Joined {new Date(e.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 self-end md:self-auto">
                    <div className="flex items-center gap-2">
                      <Switch checked={!e.disabled} onCheckedChange={() => toggleDisabled(e)} data-testid={`emp-toggle-${i}`} />
                      <span className="text-xs text-muted-foreground">{e.disabled ? "Disabled" : "Active"}</span>
                    </div>
                    <Button onClick={() => setEditing({ ...e, newPassword: "" })} variant="outline" size="sm" data-testid={`emp-edit-${i}`} className="rounded-full">
                      <Edit3 className="h-4 w-4 mr-1.5" /> Edit
                    </Button>
                    <Button onClick={() => remove(e.id)} variant="ghost" size="icon" data-testid={`emp-delete-${i}`} className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* New employee dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="new-employee-dialog">
          <DialogHeader><DialogTitle className="font-display">Add new pilot</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Full name</Label>
              <Input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Email (login)</Label>
              <Input data-testid="emp-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Phone</Label>
              <Input data-testid="emp-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Initial password</Label>
              <Input data-testid="emp-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 h-11 rounded-xl font-mono" placeholder="At least 6 characters" />
              <p className="text-xs text-muted-foreground mt-1">Share this with the pilot. They can change it later.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNew(false)}>Cancel</Button>
            <Button onClick={create} disabled={creating} data-testid="emp-create-btn" className="bg-primary hover:bg-secondary text-primary-foreground">
              {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit employee dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-employee-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit pilot</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-2 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Phone</Label>
                <Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="mt-2 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">New password (optional)</Label>
                <Input type="text" value={editing.newPassword} onChange={(e) => setEditing({ ...editing, newPassword: e.target.value })} className="mt-2 h-11 rounded-xl font-mono" placeholder="Leave blank to keep current" />
              </div>
              <div className="text-xs text-muted-foreground">Email cannot be changed: {editing.email}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
            <Button onClick={saveEdit} className="bg-primary hover:bg-secondary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
